import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Charger les notifications depuis localStorage au démarrage
  useEffect(() => {
    const stored = localStorage.getItem('notifications');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setNotifications(parsed);
        setUnreadCount(parsed.filter(n => !n.read).length);
      } catch (error) {
        console.error('Erreur chargement notifications:', error);
        setNotifications([]);
        setUnreadCount(0);
      }
    } else {
      // Pas de notifications en localStorage, initialiser à vide
      setNotifications([]);
      setUnreadCount(0);
    }
  }, []);

  // Mettre à jour le compteur de notifications non lues et sauvegarder dans localStorage
  useEffect(() => {
    const unread = notifications.filter(n => !n.read).length;
    setUnreadCount(unread);
    
    // Sauvegarder dans localStorage (même si vide, pour nettoyer les anciennes données)
    if (notifications.length > 0) {
      localStorage.setItem('notifications', JSON.stringify(notifications));
    } else {
      // Si aucune notification, nettoyer le localStorage
      localStorage.removeItem('notifications');
    }
  }, [notifications]);

  // Ajouter une notification
  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: Date.now() + Math.random(),
      type: notification.type || 'info', // 'info', 'success', 'warning', 'error'
      title: notification.title,
      message: notification.message,
      timestamp: new Date().toISOString(),
      read: false,
      category: notification.category || 'general', // 'price', 'stock', 'margin', 'general'
      actionUrl: notification.actionUrl || null,
      ...notification
    };

    setNotifications(prev => [newNotification, ...prev].slice(0, 50)); // Garder max 50 notifications
  }, []);

  // Marquer une notification comme lue
  const markAsRead = useCallback((id) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  }, []);

  // Marquer toutes comme lues
  const markAllAsRead = useCallback(() => {
    setNotifications(prev =>
      prev.map(n => ({ ...n, read: true }))
    );
    // Le useEffect se chargera de mettre à jour unreadCount automatiquement
  }, []);

  // Supprimer une notification
  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Supprimer toutes les notifications
  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
    localStorage.removeItem('notifications');
  }, []);

  // Alertes prix or
  const checkGoldPriceAlert = useCallback((currentPrice, thresholds) => {
    if (!thresholds) return;

    if (thresholds.min && currentPrice < thresholds.min) {
      addNotification({
        type: 'warning',
        category: 'price',
        title: 'Alerte Prix Or - Seuil Minimum',
        message: `Le prix de l'or (${currentPrice.toFixed(2)} USD/oz) est en dessous du seuil minimum (${thresholds.min.toFixed(2)} USD/oz)`,
        actionUrl: '/dashboard'
      });
    }

    if (thresholds.max && currentPrice > thresholds.max) {
      addNotification({
        type: 'warning',
        category: 'price',
        title: 'Alerte Prix Or - Seuil Maximum',
        message: `Le prix de l'or (${currentPrice.toFixed(2)} USD/oz) dépasse le seuil maximum (${thresholds.max.toFixed(2)} USD/oz)`,
        actionUrl: '/dashboard'
      });
    }
  }, [addNotification]);

  // Alertes stock
  const checkStockAlert = useCallback((theoreticalStock, realStock, location) => {
    if (theoreticalStock !== realStock) {
      const difference = Math.abs(theoreticalStock - realStock);
      addNotification({
        type: 'error',
        category: 'stock',
        title: `Alerte Stock - ${location.charAt(0).toUpperCase() + location.slice(1)}`,
        message: `Différence détectée : Stock théorique (${theoreticalStock}) ≠ Stock réel (${realStock}). Écart de ${difference}`,
        actionUrl: '/stock'
      });
    }
  }, [addNotification]);

  // Alertes marges
  const checkMarginAlert = useCallback((currentMargin, threshold) => {
    if (threshold && currentMargin < threshold) {
      addNotification({
        type: 'warning',
        category: 'margin',
        title: 'Alerte Marge - Seuil Critique',
        message: `La marge actuelle (${currentMargin.toFixed(2)}%) est en dessous du seuil minimum (${threshold.toFixed(2)}%)`,
        actionUrl: '/margins'
      });
    }
  }, [addNotification]);

  // Alertes PnL (latent et réalisé)
  const checkPnLAlert = useCallback((latentPnL, realizedPnL, thresholds) => {
    if (!thresholds) return;
    const lossThreshold = parseFloat(thresholds.lossThreshold);
    const profitThreshold = parseFloat(thresholds.profitThreshold);

    if (!Number.isNaN(lossThreshold) && latentPnL <= -Math.abs(lossThreshold)) {
      addNotification({
        type: 'error',
        category: 'pnl',
        title: 'Alerte PnL - Perte',
        message: `PnL latent critique: ${latentPnL.toFixed(2)} USD (seuil: -${Math.abs(lossThreshold).toFixed(2)} USD)`,
        actionUrl: '/positions'
      });
    }

    const combinedPnL = (parseFloat(latentPnL) || 0) + (parseFloat(realizedPnL) || 0);
    if (!Number.isNaN(profitThreshold) && combinedPnL >= Math.abs(profitThreshold)) {
      addNotification({
        type: 'success',
        category: 'pnl',
        title: 'Alerte PnL - Profit',
        message: `Objectif atteint: PnL combiné ${combinedPnL.toFixed(2)} USD (seuil: ${Math.abs(profitThreshold).toFixed(2)} USD)`,
        actionUrl: '/positions'
      });
    }
  }, [addNotification]);

  const value = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAll,
    checkGoldPriceAlert,
    checkStockAlert,
    checkMarginAlert,
    checkPnLAlert
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications doit être utilisé dans un NotificationProvider');
  }
  return context;
};
